import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supportApi, type CreateSupportTicketInput } from '@/api/supportApi'
import { useTenant } from '@/providers/TenantProvider'

/**
 * Title21 Support queries, keyed by the selected tenant. Tickets and threads
 * refetch every 30 s while open, so a reply from Title21 shows up without a
 * reload (the bell rings too, from the backend's webhook receiver).
 */
const KEY = 'support'
const LIVE = 30_000

export function useSupportStatus(enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'status', tenantId],
    enabled: !!tenantId && enabled,
    queryFn: () => supportApi.status(tenantId as string),
    staleTime: 60_000,
  })
}

export function useSupportTickets(enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'tickets', tenantId],
    enabled: !!tenantId && enabled,
    queryFn: () => supportApi.list(tenantId as string),
    refetchInterval: LIVE,
    retry: false,
  })
}

export function useSupportTicket(ticket: string | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'ticket', tenantId, ticket],
    enabled: !!tenantId && !!ticket,
    queryFn: () => supportApi.get(tenantId as string, ticket as string),
    refetchInterval: LIVE,
    retry: false,
  })
}

export function useSupportThread(ticket: string | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'thread', tenantId, ticket],
    enabled: !!tenantId && !!ticket,
    queryFn: () => supportApi.thread(tenantId as string, ticket as string),
    refetchInterval: LIVE,
    retry: false,
  })
}

function useRefreshSupport() {
  const qc = useQueryClient()
  return () => void qc.invalidateQueries({ queryKey: [KEY] })
}

export function useCreateSupportTicket() {
  const { selectedTenantId: tenantId } = useTenant()
  const refresh = useRefreshSupport()
  return useMutation({
    mutationFn: (input: CreateSupportTicketInput) => supportApi.create(tenantId as string, input),
    onSuccess: refresh,
  })
}

/** A reply, then its files onto that reply (one by one, so one bad file names itself). */
export function useSupportReply(ticket: string | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  const refresh = useRefreshSupport()
  return useMutation({
    mutationFn: async ({ body, files = [] }: { body: string; files?: File[] }) => {
      const posted = await supportApi.reply(tenantId as string, ticket as string, body)
      for (const f of files) await supportApi.attach(tenantId as string, ticket as string, f, posted.message.id)
      return posted
    },
    onSettled: refresh,
  })
}

/**
 * A new ticket, then its files onto the report itself (the first message of
 * the thread). If a file fails the ticket still exists: the error says which.
 */
export function useReportIssue() {
  const { selectedTenantId: tenantId } = useTenant()
  const refresh = useRefreshSupport()
  return useMutation({
    mutationFn: async ({ input, files }: { input: CreateSupportTicketInput; files: File[] }) => {
      const t = await supportApi.create(tenantId as string, input)
      if (files.length) {
        const thread = await supportApi.thread(tenantId as string, t.number)
        const first = thread.find((m) => m.author.kind === 'customer')
        for (const f of files) {
          try {
            await supportApi.attach(tenantId as string, t.number, f, first?.id)
          } catch (err) {
            throw new ReportedButFileFailed(t.number, f.name, (err as Error).message)
          }
        }
      }
      return t
    },
    onSettled: refresh,
  })
}

/** The ticket was raised; one of its files was refused. */
export class ReportedButFileFailed extends Error {
  constructor(
    readonly number: number,
    readonly fileName: string,
    reason: string,
  ) {
    super(`TKT-${String(number).padStart(5, '0')} was sent, but "${fileName}" was not attached: ${reason}`)
  }
}

/**
 * A preview link for one file (inline when its type allows). Short-lived, so
 * fetched when the viewer opens it and never cached for long.
 */
export function useSupportAttachmentLink(ticket: string, attachmentId: string, enabled: boolean) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'attachment-link', tenantId, ticket, attachmentId],
    enabled: !!tenantId && enabled,
    queryFn: () => supportApi.attachmentUrl(tenantId as string, ticket, attachmentId, true),
    staleTime: 60_000,
    gcTime: 60_000,
    retry: false,
  })
}

/**
 * Opens one of the ticket's files. The tab is opened on the click itself (a
 * tab opened after an await is blocked as a pop-up), then pointed at the
 * short-lived signed URL.
 */
export function useOpenSupportAttachment(ticket: string | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  return (attachmentId: string) => {
    const tab = window.open('about:blank', '_blank')
    supportApi
      .attachmentUrl(tenantId as string, ticket as string, attachmentId)
      .then(({ url }) => {
        if (tab) tab.location.href = url
        else window.location.href = url
      })
      .catch(() => tab?.close())
  }
}
